
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import LoadingOverlay from './LoadingOverlay';

interface NavigationControlsProps {
  currentQuestionIndex: number;
  totalQuestions: number;
  currentQuestionAnswered: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isGeneratingReport: boolean;
  submitError?: string | null;
  canEarlySubmit?: boolean;
}

const NavigationControls = ({
  currentQuestionIndex,
  totalQuestions,
  currentQuestionAnswered,
  onPrevious,
  onNext,
  onSubmit,
  isSubmitting,
  isGeneratingReport,
  submitError,
  canEarlySubmit = false
}: NavigationControlsProps) => {
  // Determine the appropriate loading message based on the current state
  const loadingMessage = isGeneratingReport 
    ? "Please wait while we analyze your responses and generate your personalized report..." 
    : "Please wait while we process your responses...";

  return (
    <>
      {/* Loading Overlay */}
      <LoadingOverlay 
        isVisible={isSubmitting || isGeneratingReport} 
        message={loadingMessage} 
      />
      
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={onPrevious}
            disabled={currentQuestionIndex === 0 || isSubmitting || isGeneratingReport}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>
          
          {(currentQuestionIndex === totalQuestions - 1 || canEarlySubmit) ? (
            <Button
              onClick={onSubmit}
              disabled={isSubmitting || isGeneratingReport || !currentQuestionAnswered}
              className="bg-brand-blue hover:bg-brand-blue/90 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : isGeneratingReport ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  Submit
                  <CheckCircle className="h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={onNext}
              disabled={!currentQuestionAnswered || isSubmitting || isGeneratingReport}
              className="bg-brand-blue hover:bg-brand-blue/90 flex items-center gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {submitError && (
          <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded-md">
            {submitError}
          </div>
        )}
        
        {canEarlySubmit && currentQuestionAnswered && (
          <div className="text-sm text-center p-2 bg-blue-50 rounded-md text-blue-600">
            You can submit your assessment now or continue with more questions.
          </div>
        )}
      </div>
    </>
  );
};

export default NavigationControls;
